import BreadCrumb from "Common/BreadCrumb";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import TableContainer from "Common/TableContainer";
import { Boxes, Loader, PackageCheck, Plus, Search } from "lucide-react";

import DeleteModal from "Common/DeleteModal";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import type { AppDispatch } from "../../store";
import { Order, RootState, TabType, TableCellProps } from "../../types/order.types";

import { CurrencyFormatter } from "./components/CurrencyFormatter";
import { DateFormatter } from "./components/DateFormatter";
import { OrderActions } from "./components/OrderActions";
import { StatusBadge } from "./components/StatusBadge";

import { useDebounce } from "../../hooks/useDebounce";
import { useErrorHandler } from "../../hooks/useErrorHandler";

import { ToastContainer } from "react-toastify";
import { deleteOrders as onDeleteOrders, getOrders as onGetOrders } from "slices/thunk";

const Orders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { isLoading, executeWithErrorHandling } = useErrorHandler();

  const selectDataList = createSelector(
    (state: RootState) => state.Orders,
    (state) => ({
      dataList: state.orders,
    })
  );

  const { dataList } = useSelector(selectDataList);

  const [data, setData] = useState<Order[]>([]);
  const [eventData, setEventData] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    const loadOrders = async () => {
      await executeWithErrorHandling(
        () => dispatch(onGetOrders()),
        undefined,
        "Carregamento de pedidos"
      );
    };

    loadOrders();
  }, [dispatch, executeWithErrorHandling]);

  const [activeStatusFilter, setActiveStatusFilter] = useState<TabType>("all");

  useEffect(() => {
    let filtered = dataList;

    if (activeStatusFilter !== "all") {
      let statusNumber = 0;
      if (activeStatusFilter === "Pendente") statusNumber = 1;
      else if (activeStatusFilter === "Sincronizado") statusNumber = 2;
      filtered = filtered.filter((order: Order) => order.status === statusNumber);
    }

    if (searchTerm.trim()) {
      const keysToSearch = [
        "id",
        "customerId",
        "paymentMethodId",
        "status",
        "customerName",
        "paymentMethodName",
      ];
      filtered = filtered.filter((item: Order) =>
        keysToSearch.some((key) => {
          const value = item[key as keyof Order];
          return value?.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    setData(filtered);
  }, [dataList, searchTerm, activeStatusFilter]);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  const onClickDelete = (order: Order) => {
    setDeleteModal(true);
    if (order?.id) {
      setEventData(order);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      const success = await executeWithErrorHandling(
        () => dispatch(onDeleteOrders(Number(eventData.id))),
        "Pedido excluído com sucesso",
        "Exclusão de pedido"
      );

      if (success) {
        setDeleteModal(false);
        setEventData(null);
      }
    }
  };

  const performSearch = useCallback((searchValue: string) => {
    setSearchTerm(searchValue.trim());
  }, []);

  const debouncedSearch = useDebounce(performSearch, 300);

  const filterSearchData = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const search = e.target.value;
      debouncedSearch(search);
    },
    [debouncedSearch]
  );

  const [activeTab, setActiveTab] = useState<string>("1");

  const toggleTab = (tab: string, type: TabType) => {
    setActiveTab(tab);
    setActiveStatusFilter(type);
  };

  const columns = useMemo(
    () => [
      {
        header: "Ações",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => (
          <OrderActions
            order={cellProps.row.original}
            rowIndex={cellProps.row.index}
            onDelete={onClickDelete}
          />
        ),
        meta: {
          style: {
            width: "80px",
            maxWidth: "80px",
            minWidth: "80px",
          },
        },
      },
      {
        header: "Cliente",
        accessorKey: "customerName",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => {
          const value = cellProps.getValue() as string;
          return value || cellProps.row.original.customerId;
        },
      },
      {
        header: "Total dos itens",
        accessorKey: "amountProducts",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => (
          <CurrencyFormatter value={cellProps.getValue() as string | number} />
        ),
        meta: {
          className: "text-right",
          style: {
            maxWidth: "200px",
            width: "150px",
          },
        },
      },
      {
        header: "Data de emissão",
        accessorKey: "dateEmission",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => (
          <DateFormatter date={cellProps.getValue() as string | Date} />
        ),
        meta: {
          className: "text-center",
          style: {
            maxWidth: "250px",
            width: "250px",
          },
        },
      },
      {
        header: "Método de Pagamento",
        accessorKey: "paymentMethodName",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => {
          const value = cellProps.getValue() as string;
          return value || cellProps.row.original.paymentMethodId;
        },
        meta: {
          className: "text-center",
          style: {
            maxWidth: "200px",
            width: "200px",
          },
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps: TableCellProps) => (
          <StatusBadge status={cellProps.getValue() as 0 | 1 | 2} />
        ),
        meta: {
          className: "text-center",
          style: {
            maxWidth: "120px",
            width: "120px",
          },
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="Pedidos de venda" pageTitle="Ecommerce" />
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="card" id="ordersTable">
        <div className="card-body" style={{ overflow: "visible" }}>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <div className="relative">
                <label htmlFor="orders-search" className="sr-only">
                  Pesquisar pedidos
                </label>
                <input
                  id="orders-search"
                  type="text"
                  role="searchbox"
                  aria-label="Pesquisar pedidos por ID, cliente, método de pagamento ou status"
                  aria-describedby={searchTerm ? "search-results" : "search-help"}
                  className={`ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 ${
                    isLoading ? "opacity-50" : ""
                  }`}
                  placeholder="Digite para pesquisar (mínimo 2 caracteres)..."
                  autoComplete="off"
                  onChange={filterSearchData}
                  disabled={isLoading}
                />
                <Search
                  className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600"
                  aria-hidden="true"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      const input = document.getElementById("orders-search") as HTMLInputElement;
                      if (input) {
                        input.value = "";
                        input.focus();
                      }
                    }}
                    className="absolute ltr:right-2.5 rtl:left-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500 rounded"
                    aria-label="Limpar pesquisa"
                    title="Limpar busca"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                )}
              </div>

              <div id="search-help" className="sr-only">
                Use este campo para pesquisar por ID do pedido, nome do cliente, método de pagamento
                ou status
              </div>
              {searchTerm && (
                <div
                  id="search-results"
                  className="mt-2 text-xs text-slate-500 dark:text-zink-400"
                  role="status"
                  aria-live="polite"
                >
                  Buscando por: <span className="font-medium">&quot;{searchTerm}&quot;</span>
                  {data.length > 0 && <span> - {data.length} resultado(s) encontrado(s)</span>}
                </div>
              )}
            </div>
            <div className="lg:col-span-2 lg:col-start-11">
              <div className="ltr:lg:text-right rtl:lg:text-left">
                <button
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:outline-none focus:ring-2 focus:ring-custom-100 focus:ring-offset-2 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  onClick={() => navigate("/sale-orders/new")}
                  aria-label="Criar novo pedido de venda"
                  disabled={isLoading}
                >
                  <Plus className="inline-block size-4" aria-hidden="true" />{" "}
                  <span className="align-middle">Novo Pedido</span>
                </button>
              </div>
            </div>
          </div>

          <div
            role="tablist"
            aria-label="Filtros de pedidos"
            className="flex flex-wrap w-full mt-5 text-sm font-medium text-center text-gray-500 nav-tabs"
          >
            <button
              role="tab"
              aria-selected={activeTab === "1"}
              aria-controls="orders-panel"
              id="tab-all"
              className={`group inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px] focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2 ${
                activeTab === "1" ? "bg-custom-500 text-white dark:text-white" : ""
              }`}
              onClick={() => toggleTab("1", "all")}
            >
              <Boxes className="inline-block size-4 ltr:mr-1 rtl:ml-1" aria-hidden="true" />{" "}
              <span className="align-middle">Todas</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "2"}
              aria-controls="orders-panel"
              id="tab-pending"
              className={`group inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px] focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2 ${
                activeTab === "2" ? "bg-custom-500 text-white dark:text-white" : ""
              }`}
              onClick={() => toggleTab("2", "Pendente")}
            >
              <Loader className="inline-block size-4 ltr:mr-1 rtl:ml-1" aria-hidden="true" />{" "}
              <span className="align-middle">Pendentes</span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === "3"}
              aria-controls="orders-panel"
              id="tab-synchronized"
              className={`group inline-block px-4 py-1.5 text-base transition-all duration-300 ease-linear rounded-md text-slate-500 dark:text-zink-200 border border-transparent hover:text-custom-500 dark:hover:text-custom-500 active:text-custom-500 dark:active:text-custom-500 -mb-[1px] focus:outline-none focus:ring-2 focus:ring-custom-500 focus:ring-offset-2 ${
                activeTab === "3" ? "bg-custom-500 text-white dark:text-white" : ""
              }`}
              onClick={() => toggleTab("3", "Sincronizado")}
            >
              <PackageCheck className="inline-block size-4 ltr:mr-1 rtl:ml-1" aria-hidden="true" />{" "}
              <span className="align-middle">Sincronizados</span>
            </button>
          </div>

          <div
            id="orders-panel"
            role="tabpanel"
            aria-labelledby={`tab-${
              activeTab === "1" ? "all" : activeTab === "2" ? "pending" : "synchronized"
            }`}
            className="mt-5"
          >
            {data && data.length > 0 ? (
              <TableContainer
                isPagination={true}
                columns={columns || []}
                data={data || []}
                customPageSize={10}
                divclassName="mt-5 overflow-x-auto min-h-[250px] relative"
                tableclassName="w-full whitespace-nowrap"
                tbodyclassName="min-h-[180px]"
                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
                tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                SearchPlaceholder="Digite para pesquisar..."
              />
            ) : (
              <div className="noresult">
                <div className="py-6 text-center">
                  <Search className="mx-auto size-6 text-sky-500 fill-sky-100 dark:sky-500/20" />
                  <h5 className="mt-2 mb-1">Desculpe, nenheum registro foi encontrado</h5>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Orders;
